import React, { useEffect, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { ReservationActivityEvent, getReservationActivity } from '../api.reservations';

interface Props {
  reservationId: number;
}

/**
 * Everything that has happened to this reservation, oldest first.
 *
 * <p>Replaces a four-item list built from four hardcoded columns. The events come from the
 * backend's append-only trail, so a part-filled group, a payment found by the nightly sweep rather
 * than the webhook, or a door code that failed to issue all show up here — none of which had
 * anywhere to appear before.
 */

/** Icon and colour per event family. Unknown types still render, so a new backend event is never
 *  invisible just because this map has not caught up. */
const LOOK: Record<string, { icon: string; tone: string }> = {
  CREATED: { icon: '📋', tone: '#64748b' },
  DATES_CHANGED: { icon: '📅', tone: '#b45309' },
  CHECKIN_STARTED: { icon: '📝', tone: '#2563eb' },
  SLOT_SUBMITTED: { icon: '👤', tone: '#2563eb' },
  FORM_COMPLETED: { icon: '✅', tone: '#15803d' },
  TAX_DUE: { icon: '💰', tone: '#b45309' },
  TAX_CHECKOUT_STARTED: { icon: '🛒', tone: '#b45309' },
  TAX_PAID: { icon: '💳', tone: '#15803d' },
  TAX_RECONCILED: { icon: '💳', tone: '#b45309' },
  TAX_SHORTFALL: { icon: '⚠️', tone: '#b91c1c' },
  TAX_EXEMPT: { icon: '🏷️', tone: '#64748b' },
  TAX_MANUAL_SET: { icon: '✍️', tone: '#64748b' },
  TAX_FAILED: { icon: '⚠️', tone: '#b91c1c' },
  GUIDE_OPENED: { icon: '👁', tone: '#15803d' },
  PIN_CREATED: { icon: '🔑', tone: '#15803d' },
  PIN_REISSUED: { icon: '🔑', tone: '#b45309' },
  PIN_FAILED: { icon: '⚠️', tone: '#b91c1c' },
  MESSAGE_SENT: { icon: '✉️', tone: '#64748b' },
  UBYPORT_SENT: { icon: '🏛', tone: '#15803d' },
  UBYPORT_PARTIAL: { icon: '🏛', tone: '#b45309' },
  UBYPORT_FAILED: { icon: '⚠️', tone: '#b91c1c' },
};

// Czech and English only. Other locales fall back to English rather than shipping machine
// translations of compliance wording nobody has checked.
const LABELS: Record<'cs' | 'en', Record<string, string>> = {
  en: {
    title: 'Activity',
    empty: 'Nothing recorded for this reservation yet.',
    CREATED: 'Reservation created',
    DATES_CHANGED: 'Dates changed',
    CHECKIN_STARTED: 'Check-in started',
    SLOT_SUBMITTED: 'Guest registered',
    FORM_COMPLETED: 'All guests registered',
    TAX_DUE: 'City tax due',
    TAX_CHECKOUT_STARTED: 'Guest opened payment',
    TAX_PAID: 'City tax paid',
    TAX_RECONCILED: 'City tax paid (found by reconciliation)',
    TAX_SHORTFALL: 'Shortfall after correction — guide re-locked',
    TAX_EXEMPT: 'Marked exempt',
    TAX_MANUAL_SET: 'Payment set manually',
    TAX_FAILED: 'Payment failed',
    GUIDE_OPENED: 'Guide opened',
    PIN_CREATED: 'Door code issued',
    PIN_REISSUED: 'Door code reissued',
    PIN_FAILED: 'Door code failed',
    MESSAGE_SENT: 'Message sent',
    UBYPORT_SENT: 'Sent to Ubyport',
    UBYPORT_PARTIAL: 'Partially sent to Ubyport',
    UBYPORT_FAILED: 'Ubyport submission failed',
  },
  cs: {
    title: 'Aktivita',
    empty: 'U této rezervace zatím nic nezaznamenáno.',
    CREATED: 'Rezervace vytvořena',
    DATES_CHANGED: 'Změna termínu',
    CHECKIN_STARTED: 'Zahájen check-in',
    SLOT_SUBMITTED: 'Host zaregistrován',
    FORM_COMPLETED: 'Všichni hosté zaregistrováni',
    TAX_DUE: 'Poplatek z pobytu k úhradě',
    TAX_CHECKOUT_STARTED: 'Host otevřel platbu',
    TAX_PAID: 'Poplatek z pobytu zaplacen',
    TAX_RECONCILED: 'Poplatek zaplacen (dohledáno kontrolou)',
    TAX_SHORTFALL: 'Nedoplatek po opravě — průvodce znovu uzamčen',
    TAX_EXEMPT: 'Označeno jako osvobozené',
    TAX_MANUAL_SET: 'Platba nastavena ručně',
    TAX_FAILED: 'Platba selhala',
    GUIDE_OPENED: 'Průvodce otevřen',
    PIN_CREATED: 'Kód ke dveřím vytvořen',
    PIN_REISSUED: 'Kód ke dveřím obnoven',
    PIN_FAILED: 'Kód ke dveřím se nepodařilo vytvořit',
    MESSAGE_SENT: 'Zpráva odeslána',
    UBYPORT_SENT: 'Odesláno do Ubyportu',
    UBYPORT_PARTIAL: 'Částečně odesláno do Ubyportu',
    UBYPORT_FAILED: 'Odeslání do Ubyportu selhalo',
  },
};

/** "slot 0 of 3", "150 CZK", "lock offline" — whatever the event carried, on one line. */
const describe = (data: Record<string, unknown>): string => {
  const parts: string[] = [];
  Object.entries(data ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (key === 'slot') {
      parts.push(`#${Number(value) + 1}`);
      return;
    }
    if (key === 'of') {
      parts.push(`/ ${value}`);
      return;
    }
    parts.push(String(value));
  });
  return parts.join(' · ');
};

const ReservationActivityTrail: React.FC<Props> = ({ reservationId }) => {
  const { lang } = useLang();
  const t = LABELS[lang === 'cs' ? 'cs' : 'en'];

  const [events, setEvents] = useState<ReservationActivityEvent[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getReservationActivity(reservationId, localStorage.getItem('token') || '')
      .then(e => { if (!cancelled) setEvents(e); })
      .catch(() => { if (!cancelled) setEvents([]); });
    return () => { cancelled = true; };
  }, [reservationId]);

  if (events === null) return null;

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', marginTop: 16, border: '1px solid #e2e8f0' }}>
      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
        {t.title}
      </div>

      {events.length === 0 ? (
        <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{t.empty}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {events.map((e, i) => {
            const look = LOOK[e.type] ?? { icon: '•', tone: '#64748b' };
            const detail = describe(e.data);
            const last = i === events.length - 1;
            return (
              <div key={`${e.at}-${e.type}-${i}`} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 22 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: '#f8fafc', border: `2px solid ${look.tone}33`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.72rem', flexShrink: 0,
                  }}>
                    {look.icon}
                  </div>
                  {!last && <div style={{ width: 2, flex: 1, background: '#e2e8f0', minHeight: 14, marginTop: 2 }} />}
                </div>
                <div style={{ paddingBottom: last ? 0 : 12, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', color: look.tone }}>
                    {t[e.type] ?? e.type}
                    {detail && <span style={{ fontWeight: 500, color: '#64748b' }}> · {detail}</span>}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                    {new Date(e.at).toLocaleString()}
                    {e.actor && e.actor !== 'system' && <span> · {e.actor}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReservationActivityTrail;
